// /* eslint-disable @typescript-eslint/no-unused-vars */
import { Store, select } from '@ngrx/store';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  switchMap,
  pluck,
  catchError,
  mergeMap,
  map,
  tap,
  take,
  filter,
  withLatestFrom,
} from 'rxjs/operators';
import { of, from } from 'rxjs';

import * as fromOffline from '../actions/offline.actions';
import * as fromOfflineSelectors from '../selectors/offline.selectors';
import { OfflineService } from '../../services/offline.service';
import { OfflineDelivery, Delivery } from '../../models/delivery.model';
import { StorageService } from '../../services/offline-storage.service';
import { DeliveryAsset } from '../../models/delivery-asset.model';
import { OfflineFileService } from '../../services/offline-file.service';
import { OfflineDownloadingService } from '../../services/offline-downloading.service';
import { loadApplicationSettingsSuccess } from 'app/settings/application-settings/store/application-settings.actions';
import { selectApplicationSettings } from 'app/settings/application-settings/store/application-settings.selectors';
import { ApplicationSettings } from 'app/settings/application-settings/store/application-settings.reducer';
import { downloadNext } from '../actions/offline-statuses.actions';
import {
  refreshUserDataSuccess,
  initUserDataAfterLoginSuccess,
} from '../actions/account-settings.actions';
import { selectOfflineDeliveries, selectOfflineDelivery } from '../selectors/offline.selectors';
import { DeliveryService } from '../../services/delivery.service';
import { ToastService } from '../../services/toast.service';

@Injectable()
export class OfflineEffects {
  actions$ = inject(Actions);
  offlineService = inject(OfflineService);
  storageService = inject(StorageService);
  offlineDwnlService = inject(OfflineDownloadingService);
  store = inject(Store);
  fileService = inject(OfflineFileService);
  toaster = inject(ToastService);
  deliveryService = inject(DeliveryService);


  setOfflineOptions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadApplicationSettingsSuccess),
        switchMap(() =>
          this.store
            .pipe(select(selectApplicationSettings))
            .pipe(filter((applicationSettings: any) => applicationSettings.loaded)),
        ),
        tap((applicationSettings: ApplicationSettings) =>
          this.store.dispatch(fromOffline.setOfflineOptions({ options: applicationSettings })),
        ),
      ),
    { dispatch: false },
  );

  setUserKey$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refreshUserDataSuccess, fromOffline.setUserKey),
        tap(() => this.storageService.setUserKey()),
      ),
    { dispatch: false },
  );

  refreshOfflineDeliveryAfterLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(initUserDataAfterLoginSuccess, fromOffline.refreshDeliveriesFromStore),
        switchMap(() =>
          from(this.storageService.getDeliveries()).pipe(
            tap((deliveries: any) => {
              this.store.dispatch(fromOffline.refreshOfflineDeliveryAfterLogin({ deliveries }));
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  refreshOfflineDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.refreshOfflineDelivery, refreshUserDataSuccess),
      switchMap(() =>
        from(this.storageService.getDeliveries()).pipe(
          map((deliveries) => fromOffline.refreshOfflineDeliverySuccess({ deliveries })),
          catchError((error: any) => of(fromOffline.refreshOfflineDeliveryFail({ error }))),
        ),
      ),
    ),
  );

  downloadNext$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(downloadNext),
        tap((resp: any) => {
          const { deliveryKeyCode, eventStatus } = resp;
          if (eventStatus === 'COMPLETED' || eventStatus === 'DAMAGED') {
            this.offlineService.dispatchAssetsDownload(deliveryKeyCode);
          }
        }),
      ),
    { dispatch: false },
  );

  refreshDownloading$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.refreshDownloading),
        tap((resp: any) => {
          const { deliveryKeyCode } = resp;
          this.offlineService.dispatchAssetsDownload(deliveryKeyCode);
        }),
      ),
    { dispatch: false },
  );

  saveDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.saveOfflineDelivery),
      pluck('delivery'),
      mergeMap((delivery: OfflineDelivery) =>
        from(this.storageService.setDelivery(delivery)).pipe(
          map(() =>
            fromOffline.getDeliveriesAudioAssets({ deliveryKeyCode: delivery.deliveryKeyCode }),
          ),
          catchError((error: any) => {
            this.toaster.showErrorInToast('', 'saveDelivery', error);

            return of(
              fromOffline.saveOfflineDeliveryFail({ deliveryKeyCode: delivery.deliveryKeyCode }),
            );
          }),
        ),
      ),
    ),
  );

  getAudioDeliveriesAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.getDeliveriesAudioAssets),
      mergeMap((data: { deliveryKeyCode: string }) =>
        this.offlineService.fetchDeliveryAudioAssets(data.deliveryKeyCode).pipe(
          map((assets: DeliveryAsset[]) => {
            if (assets && assets.length) {
              return fromOffline.saveAudioAssets({
                assets,
                deliveryKeyCode: data.deliveryKeyCode,
              });
            }

            return fromOffline.getDeliveriesAudioAssetsFail({
              deliveryKeyCode: data.deliveryKeyCode,
            });
          }),
          catchError((error: any) => {
            this.toaster.showErrorInToast('', 'getAudioDeliveriesAssets', error);
            return of(
              fromOffline.getDeliveriesAudioAssetsFail({ deliveryKeyCode: data.deliveryKeyCode }),
            );
          }),
        ),
      ),
    ),
  );

  saveAudioAssets$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.saveAudioAssets),
        withLatestFrom(this.store.select(fromOfflineSelectors.selectIsDownloading)),
        filter(([delivery, isDownloading]) => !isDownloading),
        tap(([delivery, isDownloading]) => {
          const { deliveryKeyCode } = delivery;
          this.offlineService.dispatchAssetsDownload(deliveryKeyCode);
        }),
      ),
    { dispatch: false },
  );

  downloadAudioAsset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.downloadAudioAsset),
        pluck('asset'),
        withLatestFrom(this.store.pipe(select(fromOfflineSelectors.selectIsAllowToDownload))),
        tap(([asset, isAllowToDownload]) => {
          this.offlineDwnlService.downloadAudioFile(asset, isAllowToDownload);
        }),
      ),
    { dispatch: false },
  );

  writeAudioAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.writeToAudioFile),
      mergeMap(({ file, asset }) =>
        from(this.fileService.writeAudioFile(asset, file)).pipe(
          withLatestFrom(from(this.fileService.getAudioLocalUrl(asset))),
          map(([res, assetAudioLocalUrl]) =>
            fromOffline.writeToAudioFileSuccess({
              asset: this.offlineService.getCompletedOfflineAsset(asset, assetAudioLocalUrl),
            }),
          ),
          catchError((error: any) => {
            this.toaster.showErrorInToast('', 'writeAudioAsset$', error);
            return of(fromOffline.writeToAudioFileFail());
          }),
        ),
      ),
    ),
  );

  saveDeliveriesToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromOffline.writeToAudioFileSuccess,
          fromOffline.updateOfflineDeliveries,
          fromOffline.updateOfflineAsset,
          fromOffline.updateDeliveryFromServerSuccess,
        ),
        mergeMap((type: any) =>
          this.store.pipe(
            select(fromOfflineSelectors.selectOfflinedeliveriesWithoutBasePath),
            take(1),
            tap((deliveries: OfflineDelivery[]) => {
              if (deliveries && deliveries.length) {
                this.setDeliveriesToStorage(deliveries);
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  downloadAudioAssetFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.downloadAudioAssetFail),
      pluck('deliveryKeyCode'),
      mergeMap((deliveryKeyCode: string) => {
        this.offlineDwnlService.clearDownlaodingList(deliveryKeyCode);
        return of(fromOffline.removeOfflineDelivery({ deliveryKeyCode }));
      }),
    ),
  );

  downloadDeliveryImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.downloadDeliveryImage),
      mergeMap(({ url, fieldName, deliveryKeyCode }) =>
        this.offlineService.downloadImageFile(url, deliveryKeyCode, fieldName).pipe(
          map(() => fromOffline.downloadDeliveryImageSuccess({ fieldName, deliveryKeyCode })),
          catchError(() => of(fromOffline.downloadDeliveryImageFail())),
        ),
      ),
    ),
  );

  storeImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.downloadDeliveryImageSuccess),
      mergeMap(({ fieldName, deliveryKeyCode }) =>
        from(this.fileService.getImageLocalPath(deliveryKeyCode, fieldName)).pipe(
          mergeMap((localUrl) =>
            from(this.storageService.saveImgUrl(deliveryKeyCode, fieldName, localUrl)).pipe(
              map(() =>
                fromOffline.storeDeliveryImageSuccess({
                  deliveryKeyCode,
                  fieldName,
                  localUrl,
                }),
              ),
              catchError((error: any) => of(fromOffline.storeDeliveryImageFail())),
            ),
          ),
        ),
      ),
    ),
  );

  removeOfflineDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.removeOfflineDelivery),
      mergeMap(({ deliveryKeyCode }) =>
        from(this.offlineService.removeDeliveryFromStorage(deliveryKeyCode)).pipe(
          tap(() => {
            this.offlineService.deleteFolder(deliveryKeyCode);
            this.offlineDwnlService.clearDownlaodingList(deliveryKeyCode);
          }),
          map(() => fromOffline.removeOfflineDeliverySuccess()),
          catchError((error: any) => of(fromOffline.removeOfflineDeliveryFail())),
        ),
      ),
    ),
  );

  removeOfflineDeliveryFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.removeOfflineDeliveryFail),
      map(() => fromOffline.removeOfflineDeliveryFail()),
    ),
  );

  cancelDownloading$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.cancelDownloading),
        pluck('deliveryKeyCode'),
        tap((deliveryKeyCode: string) => {
          this.offlineDwnlService.clearDownlaodingList(deliveryKeyCode);
          this.offlineService.dispatchNextDeliveryDownload(deliveryKeyCode);
          this.store.dispatch(fromOffline.updateOfflineDeliveries());
        }),
      ),
    { dispatch: false },
  );

  stopDownloading$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.stopDownloading),
        withLatestFrom(this.store.pipe(select(selectOfflineDeliveries))),
        tap(([payload, deliveries]) => {
          if (deliveries && deliveries.length) {
            this.offlineDwnlService.clearDownlaodingAllList();
            this.store.dispatch(fromOffline.updateOfflineDeliveries());
          }
        }),
      ),
    { dispatch: false },
  );

  clearAllCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromOffline.clearAllCache),
        mergeMap(() =>
          from(this.offlineService.clearAllCache()).pipe(
            map(() => fromOffline.clearAllCacheSuccess()),
            catchError((error: any) => of(fromOffline.clearAllCacheFail())),
          ),
        ),
      ),
    { dispatch: false },
  );

  setBasePath$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.setBasePath),
      mergeMap(() =>
        this.fileService.getBasePath().pipe(
          map((basePath: string) => fromOffline.setBasePathSuccess({ basePath })),
          catchError((error: any) => of(fromOffline.setBasePathFail())),
        ),
      ),
    ),
  );

  updateDeliveryFromServer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromOffline.updateDeliveryFromServer),
      switchMap(({ deliveryKeyCode }) =>
        this.store.pipe(select(selectOfflineDelivery, { deliveryKeyCode }), take(1)),
      ),
      filter(
        (offlineDelivery: OfflineDelivery) => offlineDelivery && !!offlineDelivery.deliveryKeyCode,
      ),
      mergeMap((offlineDelivery: OfflineDelivery) => {
        const { deliveryKeyCode } = offlineDelivery;
        return this.deliveryService.getDeliveryDetails(deliveryKeyCode).pipe(
          map((delivery: Delivery) => fromOffline.updateDeliveryFromServerSuccess({ delivery })),
          catchError((error: any) => of(fromOffline.updateDeliveryFromServerFail())),
        );
      }),
    ),
  );

  setDeliveriesToStorage(deliveries): Promise<any> {
    return this.storageService.setDeliveries(deliveries);
  }
}
